import React from "react";
import Sidebar from "../../components/layout/Sidebar";
import { 
  ClipboardList, 
  Settings, 
  Activity, 
  CheckCircle2, 
  Clock, 
  TrendingUp,
  ArrowUpRight
} from "lucide-react";

export default function Dashboard() {
  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />

      <main className="flex-1 p-6 md:p-10">
        {/* Header con Contexto */}
        <header className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight">
              Panel Principal
            </h2>
            <p className="text-slate-500 text-sm mt-1">
              Bienvenido. Aquí tienes el resumen operativo de hoy.
            </p>
          </div>
          
          <div className="flex gap-3">
            <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-slate-200 flex items-center gap-2 text-sm font-medium text-slate-600">
              <Clock className="w-4 h-4 text-blue-500" />
              Real-time Sync: <span className="text-emerald-500 italic">Activo</span>
            </div>
          </div>
        </header>

        {/* Mini Stats (KPIs rápidos) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {[
            { label: "Jobs Activos", val: "24", color: "text-blue-600", bg: "bg-blue-50" },
            { label: "Cumplimiento", val: "92%", color: "text-emerald-600", bg: "bg-emerald-50" },
            { label: "Alertas", val: "3", color: "text-amber-600", bg: "bg-amber-50" },
            { label: "Eficiencia", val: "+12%", color: "text-indigo-600", bg: "bg-indigo-50" },
          ].map((stat, i) => (
            <div key={i} className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{stat.label}</p>
              <div className="flex items-end justify-between mt-1">
                <span className={`text-2xl font-black ${stat.color}`}>{stat.val}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${stat.bg} ${stat.color}`}>PRO</span>
              </div>
            </div>
          ))}
        </div>

        {/* Tarjetas de Navegación / Acceso Directo */}
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-[0.2em] mb-4 ml-1">Módulos de Gestión</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          
          {/* Card: Jobs */}
          <div className="group bg-white rounded-2xl shadow-sm hover:shadow-xl hover:shadow-blue-500/10 p-6 border border-slate-200 transition-all cursor-pointer relative overflow-hidden">
            <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-100 transition-opacity">
              <ArrowUpRight className="text-blue-600" />
            </div>
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-5 shadow-lg shadow-blue-200">
              <ClipboardList className="text-white w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">Jobs</h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              Administra la ejecución diaria. Asigna tareas y supervisa el progreso del personal en campo.
            </p>
          </div>

          {/* Card: Procesos */}
          <div className="group bg-white rounded-2xl shadow-sm hover:shadow-xl hover:shadow-indigo-500/10 p-6 border border-slate-200 transition-all cursor-pointer relative overflow-hidden">
             <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-100 transition-opacity">
              <ArrowUpRight className="text-indigo-600" />
            </div>
            <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center mb-5 shadow-lg shadow-indigo-200">
              <Settings className="text-white w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors">Procesos</h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              Estructura tu operación. Define flujos de trabajo, validaciones obligatorias y flujos lógicos.
            </p>
          </div>

          {/* Card: Actividades */}
          <div className="group bg-white rounded-2xl shadow-sm hover:shadow-xl hover:shadow-emerald-500/10 p-6 border border-slate-200 transition-all cursor-pointer relative overflow-hidden">
             <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-100 transition-opacity">
              <ArrowUpRight className="text-emerald-600" />
            </div>
            <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center mb-5 shadow-lg shadow-emerald-200">
              <Activity className="text-white w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors">Actividades</h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              El detalle de la tarea. Configura checklists, captura de fotos, firmas y geolocalización.
            </p>
          </div>
        </div>

        {/* Sección de Reportes / Próximamente */}
        <div className="bg-slate-900 rounded-[2rem] p-8 text-white relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px] -mr-20 -mt-20"></div>
          
          <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
            <div className="bg-white/10 p-5 rounded-2xl backdrop-blur-md">
              <TrendingUp className="w-12 h-12 text-blue-400" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-2xl font-bold mb-2 flex items-center justify-center md:justify-start gap-3">
                Inteligencia de Datos
                <span className="text-[10px] bg-blue-600 px-2 py-0.5 rounded uppercase tracking-tighter font-black">Próximamente</span>
              </h3>
              <p className="text-slate-400 max-w-xl">
                Estamos procesando la analítica avanzada. Pronto tendrás acceso a gráficos de calor, cuellos de botella y predicción de tiempos operativos.
              </p>
            </div>
            <div className="flex gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse delay-75"></div>
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse delay-150"></div>
            </div>
          </div>
        </div>

      </main>
    </div>
  );
}